import { PHOTOS } from '../content/photos';

// Renders one of the fourteen shop-floor photographs as a <picture> with a WebP
// source and a JPEG fallback.
//
// Two things here are load-bearing and should not be "tidied":
//   * `width`/`height` always come from the largest available file, so every image
//     reserves its own space and the page keeps its CLS of 0. The site had no images
//     at all before this release, so it has spent none of its layout-shift budget.
//   * `sizes` must describe the *rendered* CSS width. Get it wrong and the browser
//     either picks a blurry candidate or downloads one far larger than it will draw.
//
// `ratio` crops the photo to a fixed aspect box with object-fit: cover — the fourteen
// images range from 1124x632 landscape to 739x1600 portrait, so the gallery needs a
// uniform tile. Omit it to render at the photo's own aspect ratio.
export default function Photo({ slug, sizes, ratio, eager = false, hero = false, style = {}, imgStyle = {} }) {
  const p = PHOTOS[slug];
  const widths = hero ? p.heroWidths : p.widths;
  const dims = hero ? p.heroDims : p.dims;
  const suffix = hero ? '-hero' : '';
  const largest = widths[widths.length - 1];
  const [w, h] = dims[largest];
  const srcSet = (ext) => widths.map((n) => `/images/${slug}${suffix}-${n}w.${ext} ${n}w`).join(', ');

  const img = (
    <img
      src={`/images/${slug}${suffix}-${largest}w.jpg`}
      srcSet={srcSet('jpg')}
      sizes={sizes}
      alt={p.alt}
      width={w}
      height={h}
      loading={eager ? 'eager' : 'lazy'}
      decoding={eager ? 'sync' : 'async'}
      {...(eager ? { fetchpriority: 'high' } : null)}
      style={{
        display: 'block',
        width: '100%',
        height: ratio ? '100%' : 'auto',
        ...(ratio ? { objectFit: 'cover' } : null),
        ...imgStyle,
      }}
    />
  );

  return (
    <picture style={{ display: 'block', ...(ratio ? { aspectRatio: ratio } : null), ...style }}>
      <source type="image/webp" srcSet={srcSet('webp')} sizes={sizes} />
      {img}
    </picture>
  );
}
